import React from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import { useAuth } from '../../context/AuthContext';

/** Client-side convenience gate for the athlete-performance pages — shows a
 * sign-in prompt instead of silently rendering nothing. This is UX only;
 * the real enforcement is Supabase RLS, which rejects unauthenticated reads
 * regardless of what the client does.
 *
 * `message` is optional — callers with more specific context (e.g. the
 * /join/:code invite page) can pass friendlier copy; the default preserves
 * the exact original wording for every other caller.
 *
 * `offerCreateAccount` is optional (default false) — when true, renders two
 * explicit paths ("Already have an account? Sign In" / "New to Football for
 * Nerds? Create Account") instead of a single ambiguous "Sign In" button.
 * Meant for cold-traffic entry points like the invite link, where a brand-
 * new guardian arriving with no prior context shouldn't have to guess
 * whether "Sign In" also covers creating an account. Both buttons open the
 * SAME AuthModal (never a duplicate form) on the tab that matches — see
 * AuthContext's authModalTab. Neither navigates away from the current page,
 * so whatever route rendered this (e.g. /join/:code) stays exactly where it
 * is; AuthModal's own emailRedirectTo/OAuth redirectTo handling (017) is
 * what brings a new guardian back here after email confirmation. */
export default function RequireAuth({ children, message, offerCreateAccount = false }) {
  const { currentUser, authLoading, setAuthModalOpen, setAuthModalTab } = useAuth();

  if (authLoading) return null;

  if (!currentUser) {
    const openCreateAccount = () => { setAuthModalTab(1); setAuthModalOpen(true); };
    // The plain single-button path below preserves its exact original
    // behavior — setAuthModalOpen(true) with no tab change — for every
    // other RequireAuth caller. Only the two-path (offerCreateAccount)
    // branch explicitly forces Sign In's tab, since it's presented
    // alongside an equally explicit Create Account option.
    const openSignIn = () => { if (offerCreateAccount) setAuthModalTab(0); setAuthModalOpen(true); };

    return (
      <Container maxWidth="sm" sx={{ py: 10, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '2.5rem', mb: 2 }}>🔒</Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Sign in required</Typography>
        <Typography sx={{ color: '#9eb8d0', mb: 3 }}>
          {message || 'Athlete profiles and testing history are only visible to signed-in guardians.'}
        </Typography>
        {offerCreateAccount ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 4 }}>
            <Box>
              <Typography sx={{ color: '#6a8aa8', fontSize: '0.8rem', mb: 1 }}>Already have an account?</Typography>
              <Button variant="contained" color="primary" onClick={openSignIn} sx={{ textTransform: 'none' }}>
                Sign In
              </Button>
            </Box>
            <Box>
              <Typography sx={{ color: '#6a8aa8', fontSize: '0.8rem', mb: 1 }}>New to Football for Nerds?</Typography>
              <Button variant="outlined" color="primary" onClick={openCreateAccount} sx={{ textTransform: 'none' }}>
                Create Account
              </Button>
            </Box>
          </Box>
        ) : (
          <Button variant="contained" color="primary" onClick={openSignIn} sx={{ textTransform: 'none' }}>
            Sign In
          </Button>
        )}
      </Container>
    );
  }

  return <Box>{children}</Box>;
}
